/**
 * Proof-points card grid shared by level and school marketing pages.
 *
 * What: Renders an eyebrow/title head over a wrapping grid of titled proof
 * cards with an optional closing line.
 * Why: Level pages surface decision-support evidence with this layout. (The
 * homepage's proof band is `LandingProofBandSection`.)
 * When: Update when the shared proof-card layout changes.
 */
import React from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import LandingCenteredSectionHead from '@/components/landing/LandingCenteredSectionHead';
import LandingSection from '@/components/landing/LandingSection';
import type { LandingSectionTone } from '@/components/landing/LandingSectionShell';
import { colors, radii, shadows } from '@/components/landing/landingTokens';

export interface ProofPointsContent {
  eyebrow: string;
  title: string;
  body?: string;
  proofPoints: Array<{ title: string; description: string }>;
  closingLine?: string;
}

interface LandingProofSectionProps {
  id: string;
  tone?: LandingSectionTone;
  content: ProofPointsContent;
  /** 'center' matches the homepage design; 'start' keeps the school layout. */
  align?: 'start' | 'center';
}

function LandingProofSection({
  content,
  id,
  tone = 'transparent',
  align = 'start',
}: LandingProofSectionProps) {
  const resolvedContent = content;
  const titleId = `${id}-title`;
  const centered = align === 'center';

  return (
    <LandingSection id={id} tone={tone} ariaLabelledby={titleId}>
      <Stack spacing={centered ? 0 : { xs: 3.5, sm: 4, md: 5 }}>
        {centered ? (
          <LandingCenteredSectionHead
            titleId={titleId}
            eyebrow={resolvedContent.eyebrow}
            title={resolvedContent.title}
            body={resolvedContent.body}
          />
        ) : (
          <Stack spacing={1.5} sx={{ maxWidth: { xs: '100%', sm: 760 } }}>
            <Typography
              variant="overline"
              sx={{
                letterSpacing: 2,
                color: colors.accentCobalt,
                fontWeight: 700,
                fontSize: 12,
              }}
            >
              {resolvedContent.eyebrow}
            </Typography>
            <Typography
              id={titleId}
              component="h2"
              sx={{
                fontSize: { xs: 28, sm: 32, md: 36 },
                fontWeight: 800,
                letterSpacing: -0.5,
                color: colors.ink,
                lineHeight: 1.15,
              }}
            >
              {resolvedContent.title}
            </Typography>
            {resolvedContent.body ? (
              <Typography
                variant="body1"
                sx={{
                  color: colors.inkMuted,
                  lineHeight: 1.65,
                  fontSize: { xs: 15, sm: 16 },
                }}
              >
                {resolvedContent.body}
              </Typography>
            ) : null}
          </Stack>
        )}

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: { xs: 2.25, sm: 2.5, md: 3 },
            width: '100%',
          }}
        >
          {resolvedContent.proofPoints.map(point => (
            <Paper
              key={point.title}
              elevation={0}
              component="article"
              sx={{
                flex: '1 1 240px',
                minWidth: 0,
                p: { xs: 2.5, sm: 3 },
                borderRadius: radii.md,
                backgroundColor: colors.paper,
                border: `1px solid ${colors.border}`,
                boxShadow: shadows.soft,
              }}
            >
              <Stack spacing={1.5}>
                <Box
                  aria-hidden
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: 999,
                    backgroundColor: alpha(colors.accentOrange, 0.92),
                  }}
                />
                <Typography
                  component="h3"
                  sx={{
                    color: colors.ink,
                    fontWeight: 800,
                    fontSize: { xs: 17, sm: 18 },
                    lineHeight: 1.3,
                  }}
                >
                  {point.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.inkMuted,
                    lineHeight: 1.65,
                    fontSize: { xs: 14, sm: 15 },
                  }}
                >
                  {point.description}
                </Typography>
              </Stack>
            </Paper>
          ))}
        </Box>

        {resolvedContent.closingLine ? (
          <Typography
            variant="body1"
            sx={{
              maxWidth: 760,
              mx: centered ? 'auto' : 0,
              // Centered mode zeroes the Stack spacing (the head carries its
              // own bottom margin), and Stack's spacing rule resets sibling
              // margins — so the gap above this line has to be padding.
              pt: centered ? { xs: 3, sm: 3.5 } : 0,
              textAlign: centered ? 'center' : 'start',
              color: colors.ink,
              fontWeight: 700,
              fontSize: { xs: 15, sm: 16 },
              lineHeight: 1.6,
            }}
          >
            {resolvedContent.closingLine}
          </Typography>
        ) : null}
      </Stack>
    </LandingSection>
  );
}

export default LandingProofSection;
